'use client';

import { Button } from '@wwe-portal/ui/components/button';
import {
  EventData,
  Milestone,
  UserEventProgress,
  UserMilestoneProgress,
} from '@wwe-portal/entities/rbe.type';
import { useTranslations } from 'next-intl';
import { FC, useMemo, useState } from 'react';

import {
  Carousel,
  CarouselArrow,
  CarouselContent,
  CarouselItem,
} from '@wwe-portal/ui/components/carousel';
import { InfoTooltip } from '@wwe-portal/ui/components/tooltip/info-tooltip';
import {
  MilestoneRewardCard,
  MilestoneRewardItem,
} from './milestone-reward-item';
import { ClaimAllButton } from './claim-all-button';
import { cn } from '@wwe-portal/ui/lib/lib';
import { Responsive } from '@wwe-portal/ui/components/responsive';

export const MilestoneRewards: FC<{
  eventData: EventData;
  userProgress: UserEventProgress | undefined;
}> = ({ eventData, userProgress }) => {
  const t = useTranslations();

  return (
    <div>
      <div className="flex items-center justify-between md:mb-8">
        <div className="flex items-center gap-2">
          <h3 className="text-text300 text-xl font-semibold">
            Milestone Rewards
          </h3>
          <div className="text-text600 hidden text-sm md:block">
            ({t('sent_in_inbox')})
          </div>
        </div>
        <div className="flex items-center gap-2">
          {eventData.pointRules && eventData.pointRules.length > 0 && (
            <InfoTooltip
              content={
                <ul className="text-text500 list-inside list-disc capitalize">
                  {eventData.pointRules.map((item) => (
                    <li key={item.id}>{item.description}</li>
                  ))}
                </ul>
              }
              contentProps={{
                side: 'bottom',
                style: {
                  maxWidth: 220,
                },
              }}
            />
          )}
          <ClaimAllButton userProgress={userProgress} eventData={eventData} />
        </div>
      </div>
      <div className="text-text600 mb-4 text-sm md:hidden">
        ({t('sent_in_inbox')})
      </div>
      <MilestoneRewardList eventData={eventData} userProgress={userProgress} />
    </div>
  );
};

const MilestoneRewardList: FC<{
  eventData: EventData;
  userProgress: UserEventProgress | undefined;
}> = ({ eventData, userProgress }) => {
  const t = useTranslations();
  const [showMore, setShowMore] = useState(false);
  const userMilestones = useMemo(() => {
    return eventData.milestones.map((milestone) => ({
      data: milestone,
      progress: userProgress?.milestones.find(
        (userMilestone) => userMilestone.id === milestone.id
      ),
    }));
  }, [eventData.milestones, userProgress]);

  const sortedMilestones = useMemo(() => {
    const unclaimedMilestones = userMilestones.filter(
      (milestone) =>
        milestone.progress?.type == null || milestone.progress?.type === 'claim'
    );
    const disabledMilestones = userMilestones.filter(
      (milestone) => milestone.progress?.type === 'not.ready'
    );
    const claimedMilestones = userMilestones.filter(
      (milestone) => milestone.progress?.type === 'claimed'
    );

    return [
      ...unclaimedMilestones,
      ...disabledMilestones,
      ...claimedMilestones,
    ];
  }, [userMilestones]);

  const top3Milestones = useMemo(
    () => sortedMilestones.slice(0, 3),
    [sortedMilestones]
  );
  const otherMilestones = useMemo(
    () => sortedMilestones.slice(3),
    [sortedMilestones]
  );

  const mobileContent = (
    <div className="flex flex-col gap-2.5 md:hidden">
      {top3Milestones.map((item) => (
        <MilestoneRewardItem
          key={item.data.id}
          data={item.data}
          userProgress={item.progress}
          event={eventData}
          milestoneId={item.data.id}
          totalPoints={userProgress?.point}
        />
      ))}
      {showMore &&
        otherMilestones.map((item) => (
          <MilestoneRewardItem
            key={item.data.id}
            data={item.data}
            userProgress={item.progress}
            event={eventData}
            milestoneId={item.data.id}
            totalPoints={userProgress?.point}
          />
        ))}
      {!showMore && otherMilestones.length > 0 && (
        <Button
          variant="secondary"
          size="large"
          onClick={() => setShowMore(true)}
        >
          {t('view_more')}
        </Button>
      )}
    </div>
  );

  const desktopContent = (
    <MilestoneRewardCarousel
      milestones={userMilestones}
      eventData={eventData}
      totalPoints={userProgress?.point}
      className="hidden md:block"
    />
  );

  return (
    <Responsive
      sm={desktopContent}
      loading={
        <>
          {mobileContent}
          {desktopContent}
        </>
      }
    >
      {mobileContent}
    </Responsive>
  );
};

export const MilestoneRewardCarousel: FC<{
  milestones: {
    data: Milestone;
    progress: UserMilestoneProgress | undefined;
  }[];
  totalPoints: number | undefined;
  eventData: EventData;
  className?: string;
}> = ({ milestones, eventData, totalPoints, className }) => {
  const startIndex = useMemo(() => {
    const firstClaimableIndex = milestones.findLastIndex(
      (milestone) => milestone.progress?.type === 'claimed'
    );
    if (firstClaimableIndex < 0) return 0;
    return Math.min(firstClaimableIndex + 1, milestones.length - 1);
  }, [milestones]);

  return (
    <Carousel
      opts={{
        startIndex,
        align: 'start',
        loop: false,
      }}
      className={cn('mx-[-30px] overflow-hidden', className)}
    >
      <CarouselContent className="ml-[30px] mr-[10px]">
        {milestones.map((item) => (
          <CarouselItem
            key={item.data.id}
            className="3xl:basis-[23%] basis-[36%] pl-0 pr-5 2xl:basis-[30%]"
          >
            <MilestoneRewardCard
              key={item.data.id}
              data={item.data}
              userProgress={item.progress}
              event={eventData}
              milestoneId={item.data.id}
              totalPoints={totalPoints}
            />
          </CarouselItem>
        ))}
      </CarouselContent>
      <CarouselArrow />
    </Carousel>
  );
};
